import { SupportedLanguages } from "./supported_languages.js";
import { GlobalSettings } from "../global_settings.js";
import { PersistentCache } from "../offline/persistent_cache.js";

const REMOTE_CACHE_KEY = "localization_remote_v1";
const REMOTE_ETAG_KEY = "localization_remote_etag_v1";
// import { DevToDev } from "../plugins/dev_to_dev_plugin.js";

type LocalizationRecord = Record<SupportedLanguages, string>;

export class LocalizationManager
{
  private static readonly PATH_TO_CSV = 'configs/localization.csv';
  private static instance: LocalizationManager;
  private static language_keys: SupportedLanguages[] = Object.values( SupportedLanguages ) as SupportedLanguages[];
  private static readonly language_set: Set<string> = new Set( LocalizationManager.language_keys );

  private localization_map = new Map<string, LocalizationRecord>();
  private has_cached_remote = false;

  private static current_language: SupportedLanguages = SupportedLanguages.DEFAULT_LANGUAGE;
  private static listeners: Array<( lang: SupportedLanguages ) => void> = [];

  private constructor()
  {
    this.loadCsvSync();
  }
  public static resolveSupportedLanguage( candidate?: string | null ): SupportedLanguages | null
  {
    if ( !candidate )
    {
      return null;
    }

    const normalized = candidate.toLowerCase().replace( /_/g, '-' );
    if ( LocalizationManager.language_set.has( normalized ) )
    {
      return normalized as SupportedLanguages;
    }

    const base = normalized.split( '-' )[0];
    if ( LocalizationManager.language_set.has( base ) )
    {
      return base as SupportedLanguages;
    }

    return null;
  }

  public static detectLanguageFromNavigator(): SupportedLanguages | null
  {
    const nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator;
    if ( !nav )
    {
      return null;
    }

    const candidates: Array<string | undefined | null> = [];
    if ( Array.isArray( nav.languages ) )
    {
      candidates.push( ...nav.languages );
    }

    candidates.push(
      nav.language,
      ( nav as any ).userLanguage,
      ( nav as any ).browserLanguage
    );

    for ( const candidate of candidates )
    {
      const match = LocalizationManager.resolveSupportedLanguage( candidate );
      if ( match )
      {
        return match;
      }
    }

    return null;
  }

  private loadCsvSync(): void
  {
    const xhr = new XMLHttpRequest();
    xhr.open( 'GET', LocalizationManager.PATH_TO_CSV, false );
    xhr.send();
    if ( xhr.status !== 200 )
    {
      console.error( `Localization: failed to load TSV: ${xhr.status}` );
      return;
    }

    this.parseTsv( xhr.responseText );
  }

  // merge=false (initial bundled load) replaces the map. merge=true (remote
  // load) overlays remote rows on top of the bundled data per-key, so keys that
  // ship bundled but aren't in the remote table (e.g. newly added UI strings)
  // keep their bundled fallback instead of vanishing.
  private parseTsv( tsv: string, merge: boolean = false ): void
  {
    if ( !merge )
      this.localization_map.clear();

    const lines = tsv.split( /\r?\n/ ).filter( l => l.trim() );
    const headers_raw = lines[0].split( '\t' ).map( h => h.trim() );
    const headers = headers_raw.map( h => h.toLowerCase() );

    if ( headers[0] !== 'key' )
    {
      console.warn(
        `Localization: first column should be "Key", found "${headers_raw[0]}"`
      );
    }

    const actual_langs = headers.slice( 1 );
    const expected_langs = LocalizationManager.language_keys;

    expected_langs
      .filter( lang => !actual_langs.includes( lang ) )
      .forEach( l =>
        console.error( `Localization: missing column for language "${l}"` )
      );

    actual_langs
      .filter( l => !expected_langs.includes( l as SupportedLanguages ) )
      .forEach( l =>
        console.warn( `Localization: unsupported language "${l}" in TSV` )
      );

    const header_index = new Map<string, number>();
    headers.forEach( ( h, i ) => header_index.set( h, i ) );

    for ( const line of lines.slice( 1 ) )
    {
      const cols = line.split( '\t' );
      const key = cols[0].trim();
      const record = {} as LocalizationRecord;

      for ( const lang of expected_langs )
      {
        const idx = header_index.get( lang );
        let text = '';
        if ( idx !== undefined && idx < cols.length )
        {
          text = cols[idx].trim();
        }
        text = text.replace( /\\n/g, '\n' );
        record[lang] = text;
      }

      this.localization_map.set( key, record );
    }
  }

  public static getAvailableLanguages(): SupportedLanguages[]
  {
    return [...LocalizationManager.language_keys];
  }

  public static getInstance(): LocalizationManager
  {
    if ( !LocalizationManager.instance )
    {
      LocalizationManager.instance = new LocalizationManager();
    }
    return LocalizationManager.instance;
  }

  // Fetches the server-managed translation table and, on success, overlays it
  // on top of the bundled CSV (remote wins per-key; bundled-only keys survive).
  // Any failure (network, timeout, 404 when no admin has configured remote
  // localization yet) is swallowed and the bundled fallback stays authoritative.
  public static async loadRemote(): Promise<void>
  {
    const instance = LocalizationManager.getInstance();
    instance.applyCachedRemote();
    const controller = new AbortController();
    const timeout_id = setTimeout( () => controller.abort(), 2500 );

    try
    {
      const url = `${GlobalSettings.API_BASE_URL}/api/localization`;
      const cached_etag = instance.has_cached_remote ? PersistentCache.read<string>( REMOTE_ETAG_KEY ) : null;
      const res = await fetch( url, {
        signal: controller.signal,
        headers: cached_etag ? { 'if-none-match': cached_etag } : {},
      } );
      if ( res.status === 304 )
      {
        console.log( 'Localization: remote translations unchanged' );
        return;
      }
      if ( !res.ok )
      {
        throw new Error( `HTTP ${res.status}` );
      }
      const tsv = await res.text();
      instance.parseTsv( tsv, true );
      PersistentCache.write( REMOTE_CACHE_KEY, tsv );
      const etag = res.headers.get( 'etag' );
      if ( etag ) PersistentCache.write( REMOTE_ETAG_KEY, etag );
      else PersistentCache.remove( REMOTE_ETAG_KEY );
      instance.has_cached_remote = true;
      console.log( 'Localization: loaded remote translations' );
    }
    catch ( e )
    {
      console.warn( 'Localization: remote fetch failed, keeping bundled data', e );
    }
    finally
    {
      clearTimeout( timeout_id );
    }
  }

  private applyCachedRemote(): void
  {
    const cached = PersistentCache.read<string>( REMOTE_CACHE_KEY );
    if ( typeof cached !== 'string' || !cached.trim() ) return;
    try
    {
      this.parseTsv( cached, true );
      this.has_cached_remote = true;
      console.log( 'Localization: applied cached remote translations' );
    }
    catch ( e )
    {
      console.warn( 'Localization: cached remote table is unusable, dropping it', e );
      PersistentCache.remove( REMOTE_CACHE_KEY );
    }
  }

  private static remote_ready: Promise<void> = Promise.resolve();

  public static setRemoteReadyPromise( promise: Promise<void> ): void
  {
    this.remote_ready = promise;
  }

  public static whenRemoteReady(): Promise<void>
  {
    return this.remote_ready;
  }

  public static setLanguage( lang: SupportedLanguages ): void
  {
    if ( lang === this.current_language ) return;
    this.current_language = lang;
    // try { void DevToDev.sendEvent(`language_set_${lang}`); } catch {}
    for ( const cb of this.listeners )
    {
      cb( lang );
    }
  }

  public static getSavedLanguage(): SupportedLanguages | null
  {
    try
    {
      return this.resolveSupportedLanguage(
        localStorage.getItem( "localization_language" )
        ?? localStorage.getItem( "language" )
      );
    }
    catch
    {
      return null;
    }
  }

  public static getCurrentLanguage(): SupportedLanguages
  {
    return this.current_language;
  }

  public static subscribe( cb: ( lang: SupportedLanguages ) => void ): void
  {
    this.listeners.push( cb );
  }

  public static unsubscribe( cb: ( lang: SupportedLanguages ) => void ): void
  {
    this.listeners = this.listeners.filter( x => x !== cb );
  }

  public getText( lang: SupportedLanguages, key: string ): string
  {
    const record = this.localization_map.get( key );
    if ( !record )
    {
      console.error( `Localization: key "${key}" not found` );
      return key;
    }

    const text_for_lang = record[lang];
    if ( text_for_lang )
    {
      return text_for_lang;
    }

    const default_text = record[SupportedLanguages.DEFAULT_LANGUAGE];
    if ( default_text )
    {
      console.warn(
        `Localization: missing "${lang}" for key "${key}", using default`
      );
      return default_text;
    }

    console.error(
      `Localization: no translation for "${key}" in any language`
    );
    return key;
  }
  public static t( key: string ): string
  {
    return LocalizationManager.getInstance().getText(
      LocalizationManager.getCurrentLanguage(),
      key
    );
  }

  public static has( key: string ): boolean
  {
    return LocalizationManager.getInstance().localization_map.has( key );
  }

  public static tWithParams( key: string, params: readonly string[] ): string
  {
    const template = LocalizationManager.t( key );
    return template.replace( /\{(\d+)\}/g, ( match, index_text ) =>
    {
      const index = Number.parseInt( index_text, 10 );
      return index >= 0 && index < params.length ? params[index] : match;
    } );
  }
}
